import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../Config/config';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import CollegeHeader from './CollegeHeader';
import { Search, Users, ChevronDown, X, RefreshCw, AlertCircle } from 'lucide-react';

// --- Branch list --------------------------------------------------------------
const BRANCHES = [
  { code: 'CSE',     name: 'Computer Science Engineering (CSE)' },
  { code: 'IT',      name: 'Information Technology (IT)' },
  { code: 'AI_ML',   name: 'Artificial Intelligence & Machine Learning (AI & ML)' },
  { code: 'AI_DS',   name: 'Artificial Intelligence & Data Science (AI & DS)' },
  { code: 'DS',      name: 'Data Science' },
  { code: 'CS',      name: 'Cyber Security' },
  { code: 'ECE',     name: 'Electronics & Communication Engineering (ECE)' },
  { code: 'EEE',     name: 'Electrical & Electronics Engineering (EEE)' },
  { code: 'EE',      name: 'Electrical Engineering (EE)' },
  { code: 'ME',      name: 'Mechanical Engineering (ME)' },
  { code: 'CIVIL',   name: 'Civil Engineering' },
  { code: 'CHEM',    name: 'Chemical Engineering' },
  { code: 'BIOTECH', name: 'Biotechnology' },
  { code: 'AERO',    name: 'Aerospace Engineering' },
  { code: 'AUTO',    name: 'Automobile Engineering' },
  { code: 'MECH',    name: 'Mechatronics' },
  { code: 'ROBO',    name: 'Robotics & Automation' },
  { code: 'AGRI',    name: 'Agricultural Engineering' },
  { code: 'PETRO',   name: 'Petroleum Engineering' },
  { code: 'MINING',  name: 'Mining Engineering' },
  { code: 'MET',     name: 'Metallurgical Engineering' },
  { code: 'BIO_MED', name: 'Biomedical Engineering' },
  { code: 'ENV',     name: 'Environmental Engineering' },
  { code: 'INST',    name: 'Instrumentation Engineering' },
  { code: 'TEXTILE', name: 'Textile Engineering' },
  { code: 'PROD',    name: 'Production Engineering' },
  { code: 'IND',     name: 'Industrial Engineering' },
  { code: 'MARINE',  name: 'Marine Engineering' },
  { code: 'ARCH',    name: 'Architecture' },
  { code: 'BCA',     name: 'BCA' },
  { code: 'MCA',     name: 'MCA' },
  { code: 'BSC_CS',  name: 'BSc Computer Science' },
  { code: 'BSC_IT',  name: 'BSc Information Technology' },
  { code: 'BSC_DS',  name: 'BSc Data Science' },
  { code: 'BBA',     name: 'BBA' },
  { code: 'MBA',     name: 'MBA' },
  { code: 'COMMERCE',name: 'Commerce' },
  { code: 'ARTS',    name: 'Arts & Humanities' },
  { code: 'DIP_CSE', name: 'Diploma CSE' },
  { code: 'DIP_ECE', name: 'Diploma ECE' },
  { code: 'DIP_MECH',name: 'Diploma Mechanical' },
  { code: 'DIP_CIVIL',name:'Diploma Civil' },
  { code: 'POLY',    name: 'Polytechnic' },
  { code: 'OTHER',   name: 'Other' },
];

const sortedBranches = [...BRANCHES].sort((a, b) => {
  if (a.code === 'OTHER') return 1;
  if (b.code === 'OTHER') return -1;
  return a.name.localeCompare(b.name);
});

// --- Design tokens ------------------------------------------------------------
const T = {
  border: '1px solid var(--border-subtle, #E2E8F0)',
  radius: { sm: '8px', md: '10px', lg: '14px', xl: '18px', full: '999px' },
  shadow: { xs: '0 1px 3px rgba(0,0,0,0.06)', sm: '0 2px 8px rgba(0,0,0,0.08)' },
};

// --- Branch multi-select dropdown ---------------------------------------------
const BranchDropdown = ({ selected, onToggle, onClear }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = sortedBranches.filter(b =>
    b.name.toLowerCase().includes(search.toLowerCase()) ||
    b.code.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ position: 'relative', minWidth: 280 }}
      onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setOpen(false); }}
      tabIndex={-1}
    >
      {/* Trigger */}
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '9px 12px', background: '#FFFFFF',
          border: open ? '1px solid var(--primary-blue, #4F46E5)' : T.border,
          borderRadius: T.radius.md, cursor: 'pointer',
          boxShadow: open ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none',
          transition: 'all 0.15s', minHeight: 40,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1, overflow: 'hidden' }}>
          {selected.length === 0 ? (
            <span style={{ fontSize: '0.875rem', color: '#94A3B8' }}>Select Branch</span>
          ) : (
            selected.slice(0, 2).map(b => (
              <span key={b.code} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '2px 8px', background: '#EFF6FF', color: 'var(--primary-blue, #4F46E5)',
                border: '1px solid #BFDBFE', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600,
              }}>
                {b.code}
                <X size={11} style={{ cursor: 'pointer' }} onClick={e => { e.stopPropagation(); onToggle(b); }} />
              </span>
            ))
          )}
          {selected.length > 2 && (
            <span style={{ fontSize: '0.75rem', color: 'var(--primary-blue, #4F46E5)', fontWeight: 700, alignSelf: 'center' }}>
              +{selected.length - 2} more
            </span>
          )}
        </div>
        <ChevronDown size={15} style={{ color: '#94A3B8', flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 100,
          background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg,
          boxShadow: '0 8px 24px rgba(0,0,0,0.1)', maxHeight: 260, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '8px 10px', borderBottom: T.border }}>
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search branches..."
              style={{
                width: '100%', padding: '7px 10px', border: T.border, borderRadius: T.radius.md,
                fontSize: '0.8125rem', outline: 'none', boxSizing: 'border-box',
              }}
              onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-subtle, #E2E8F0)'; }}
            />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 ? (
              <div style={{ padding: '12px 14px', fontSize: '0.8125rem', color: '#94A3B8', fontStyle: 'italic' }}>No branches found</div>
            ) : filtered.map(b => {
              const isSel = selected.some(s => s.code === b.code);
              return (
                <div key={b.code} onClick={() => onToggle(b)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 14px', cursor: 'pointer',
                    background: isSel ? '#F0F9FF' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = '#F8FAFC'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input type="checkbox" readOnly checked={isSel}
                    style={{ width: 14, height: 14, accentColor: 'var(--primary-blue, #4F46E5)', flexShrink: 0, cursor: 'pointer' }} />
                  <span style={{ fontSize: '0.8125rem', color: isSel ? 'var(--secondary-blue, #1E3A8A)' : '#374151', fontWeight: isSel ? 600 : 400 }}>
                    {b.name}
                  </span>
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ padding: '8px 14px', borderTop: T.border, background: '#F8FAFC', borderRadius: `0 0 ${T.radius.lg} ${T.radius.lg}` }}>
              <button onClick={e => { e.stopPropagation(); onClear(); }}
                style={{ fontSize: '0.75rem', color: 'var(--primary-blue, #4F46E5)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                Clear all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- Main component -----------------------------------------------------------
const CollegeTotalStudents = () => {
  const [students,        setStudents]        = useState([]);
  const [loading,         setLoading]         = useState(true);
  const [error,           setError]           = useState(null);
  const [filterType,      setFilterType]      = useState('Branch');
  const [selectedBranches,setSelectedBranches]= useState([]);
  const [selectedOption,  setSelectedOption]  = useState('');
  const [searchTerm,      setSearchTerm]      = useState('');

  const technologyOptions = ['React', 'Node.js', 'Python'];
  const levelOptions = ['Level 1 - Level 5', 'Level 6 - Level 10', 'Level 11 - Level 15', 'Level 16 - Level 20'];

  const fetchStudents = async () => {
    const user = JSON.parse(localStorage.getItem('user'));
    const collegeName = user?.name;
    if (!collegeName) return;
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_BASE_URL}/colleges/students/${encodeURIComponent(collegeName)}`);
      setStudents(res.data);
    } catch (err) {
      console.error('Failed to fetch students:', err);
      setError('Failed to load students. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStudents(); }, []);

  const handleFilterTypeChange = (e) => {
    setFilterType(e.target.value);
    setSelectedOption('');
    setSelectedBranches([]);
  };

  const toggleBranch = (branch) => {
    setSelectedBranches(prev =>
      prev.some(b => b.code === branch.code)
        ? prev.filter(b => b.code !== branch.code)
        : [...prev, branch]
    );
  };

  const getOptions = () => {
    switch (filterType) {
      case 'Branch':      return sortedBranches;
      case 'Technologies':return technologyOptions;
      case 'Levels':      return levelOptions;
      default:            return [];
    }
  };

  const filteredStudents = (() => {
    let filtered = [...students];
    if (filterType === 'Branch' && selectedBranches.length > 0) {
      const names = selectedBranches.map(b => b.name);
      const codes = selectedBranches.map(b => b.code);
      filtered = filtered.filter(s => names.includes(s.branch) || codes.includes(s.branch));
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.first_name?.toLowerCase().includes(q) ||
        s.last_name?.toLowerCase().includes(q) ||
        s.email?.toLowerCase().includes(q)
      );
    }
    return filtered;
  })();

  const inputStyle = {
    width: '100%', padding: '9px 12px', fontSize: '0.875rem', fontWeight: 500,
    background: '#FFFFFF', border: T.border, borderRadius: T.radius.md,
    color: '#334155', outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s, box-shadow 0.15s',
  };
  const labelStyle = {
    display: 'block', fontSize: '0.65rem', fontWeight: 700, color: '#64748B',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
  };

  return (
    <div className="college-main-layout" style={{ background: '#F8FAFC' }}>
      <CollegeHeader />

      <div className="college-content-wrapper">
        <CollegeHomeSideBar />

        <main className="college-main-content" style={{ padding: '32px 28px' }}>
          <div style={{ maxWidth: 1280, margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, marginBottom: 28, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 48, height: 48, borderRadius: '12px', background: 'rgba(79, 70, 229,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Users size={22} style={{ color: 'var(--primary-blue, #4F46E5)' }} />
                </div>
                <div>
                  <h1 style={{ fontSize: '1.625rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em', margin: 0 }}>Student Directory</h1>
                  <p style={{ color: '#64748B', fontSize: '0.875rem', margin: '3px 0 0' }}>
                    {loading ? 'Loading...' : `${filteredStudents.length} student${filteredStudents.length !== 1 ? 's' : ''} found`}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Link to="/StudentsProgress" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px', borderRadius: T.radius.md,
                  background: '#FFFFFF', border: T.border, textDecoration: 'none',
                  fontSize: '0.8125rem', fontWeight: 600, color: '#374151',
                  boxShadow: T.shadow.xs, transition: 'background 0.15s',
                }}>
                  View Insights ->
                </Link>
                <button onClick={fetchStudents} style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: T.radius.md,
                  background: '#FFFFFF', border: T.border, cursor: 'pointer',
                  fontSize: '0.8125rem', fontWeight: 600, color: '#64748B',
                  boxShadow: T.shadow.xs, transition: 'background 0.15s',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                  onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
                >
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            </div>

            {/* Filter bar */}
            <div style={{
              background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg,
              padding: '20px', marginBottom: 20, boxShadow: T.shadow.xs,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, var(--primary-blue, #4F46E5), #7C3AED)', borderRadius: `${T.radius.lg} ${T.radius.lg} 0 0` }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end', marginTop: 4 }}>

                {/* Search */}
                <div style={{ flex: '1 1 240px', minWidth: 200 }}>
                  <label style={labelStyle}>Search</label>
                  <div style={{ position: 'relative' }}>
                    <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94A3B8', pointerEvents: 'none' }} />
                    <input
                      type="text"
                      placeholder="Name or email..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      style={{ ...inputStyle, paddingLeft: 32 }}
                      onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
                      onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; }}
                    />
                  </div>
                </div>

                {/* Filter type */}
                <div style={{ flex: '0 0 160px' }}>
                  <label style={labelStyle}>Filter By</label>
                  <select value={filterType} onChange={handleFilterTypeChange}
                    style={{ ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
                    onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
                    onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; }}
                  >
                    <option value="Branch">Branch</option>
                    <option value="Technologies">Technologies</option>
                    <option value="Levels">Levels</option>
                  </select>
                </div>

                {/* Filter value */}
                <div style={{ flex: '1 1 280px' }}>
                  <label style={labelStyle}>{filterType}</label>
                  {filterType === 'Branch' ? (
                    <BranchDropdown
                      selected={selectedBranches}
                      onToggle={toggleBranch}
                      onClear={() => setSelectedBranches([])}
                    />
                  ) : (
                    <select value={selectedOption} onChange={e => setSelectedOption(e.target.value)}
                      style={{ ...inputStyle, cursor: 'pointer', appearance: 'none', WebkitAppearance: 'none' }}
                      onFocus={e => { e.target.style.borderColor = 'var(--primary-blue, #4F46E5)'; e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229,0.1)'; }}
                      onBlur={e  => { e.target.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.target.style.boxShadow = 'none'; }}
                    >
                      <option value="">Select {filterType}</option>
                      {getOptions().map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  )}
                </div>

                {/* Active filters pill */}
                {(selectedBranches.length > 0 || selectedOption) && (
                  <button
                    onClick={() => { setSelectedBranches([]); setSelectedOption(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '9px 14px', borderRadius: T.radius.full,
                      background: '#FEF2F2', border: '1px solid #FECACA',
                      color: '#DC2626', fontSize: '0.8rem', fontWeight: 600,
                      cursor: 'pointer', transition: 'background 0.15s', alignSelf: 'flex-end',
                    }}
                  >
                    <X size={13} /> Clear filters
                  </button>
                )}
              </div>
            </div>

            {/* Table card */}
            <div style={{
              background: '#FFFFFF', border: T.border, borderRadius: T.radius.lg,
              boxShadow: T.shadow.sm, overflow: 'hidden', position: 'relative',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'var(--brand-gradient, linear-gradient(135deg, #1E3A8A, #4F46E5))', zIndex: 1 }} />

              {/* Loading */}
              {loading && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 24px', flexDirection: 'column', gap: 14, marginTop: 4 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', border: '3px solid #BFDBFE', borderTopColor: 'var(--primary-blue, #4F46E5)', animation: 'spin 0.7s linear infinite' }} />
                  <p style={{ color: '#64748B', fontSize: '0.875rem', margin: 0 }}>Loading students...</p>
                </div>
              )}

              {/* Error */}
              {!loading && error && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px 24px', background: '#FEF2F2', margin: 16, borderRadius: T.radius.md, border: '1px solid #FECACA', marginTop: 20 }}>
                  <AlertCircle size={20} style={{ color: '#DC2626', flexShrink: 0 }} />
                  <p style={{ color: '#991B1B', fontSize: '0.875rem', margin: 0 }}>{error}</p>
                </div>
              )}

              {/* Table */}
              {!loading && !error && (
                <div style={{ overflowX: 'auto', marginTop: 4 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: '#F8FAFC', borderBottom: T.border }}>
                        {['Student', 'Email', 'Branch', 'Technology', 'Level'].map((h, i) => (
                          <th key={h} style={{
                            padding: '12px 16px', textAlign: 'left',
                            fontSize: '0.65rem', fontWeight: 700, color: 'var(--primary-blue, #4F46E5)',
                            textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.length === 0 ? (
                        <tr>
                          <td colSpan={5} style={{ padding: '64px 24px', textAlign: 'center' }}>
                            <div style={{ width: 52, height: 52, borderRadius: '14px', background: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
                              <Users size={24} style={{ color: '#CBD5E1' }} />
                            </div>
                            <p style={{ fontWeight: 700, color: '#374151', margin: '0 0 4px' }}>No students found</p>
                            <p style={{ color: '#94A3B8', fontSize: '0.875rem', margin: 0 }}>
                              {searchTerm || selectedBranches.length > 0 ? 'Try adjusting your filters.' : 'No students are registered yet.'}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredStudents.map((student, idx) => (
                          <tr key={idx}
                            style={{ borderBottom: T.border, background: '#FFFFFF', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#F8FAFC'}
                            onMouseLeave={e => e.currentTarget.style.background = '#FFFFFF'}
                          >
                            {/* Student */}
                            <td style={{ padding: '13px 16px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <div style={{
                                  width: 34, height: 34, borderRadius: '50%',
                                  background: 'rgba(79, 70, 229,0.1)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '0.75rem', fontWeight: 800, color: 'var(--primary-blue, #4F46E5)', flexShrink: 0,
                                }}>
                                  {(student.first_name || '?')[0].toUpperCase()}
                                </div>
                                <Link
                                  to={`/progress/${encodeURIComponent(student.email)}`}
                                  style={{ fontWeight: 700, color: '#0F172A', fontSize: '0.875rem', textDecoration: 'none', transition: 'color 0.15s' }}
                                  onMouseEnter={e => e.target.style.color = 'var(--primary-blue, #4F46E5)'}
                                  onMouseLeave={e => e.target.style.color = '#0F172A'}
                                >
                                  {student.first_name} {student.last_name}
                                </Link>
                              </div>
                            </td>
                            {/* Email */}
                            <td style={{ padding: '13px 16px', fontSize: '0.8125rem', color: '#64748B' }}>{student.email}</td>
                            {/* Branch */}
                            <td style={{ padding: '13px 16px' }}>
                              <span style={{
                                padding: '3px 10px', borderRadius: T.radius.full,
                                background: '#EFF6FF', color: 'var(--primary-blue, #4F46E5)',
                                border: '1px solid #BFDBFE',
                                fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em',
                              }}>
                                {student.branch || 'N/A'}
                              </span>
                            </td>
                            {/* Technology */}
                            <td style={{ padding: '13px 16px' }}>
                              <span style={{
                                padding: '3px 10px', borderRadius: T.radius.full,
                                background: '#F3E8FF', color: '#7C3AED',
                                border: '1px solid #E9D5FF',
                                fontSize: '0.72rem', fontWeight: 700,
                              }}>
                                -
                              </span>
                            </td>
                            {/* Level */}
                            <td style={{ padding: '13px 16px' }}>
                              <span style={{
                                padding: '3px 10px', borderRadius: T.radius.full,
                                background: '#DCFCE7', color: '#16A34A',
                                border: '1px solid #BBF7D0',
                                fontSize: '0.72rem', fontWeight: 700,
                              }}>
                                -
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </main>
      </div>
    </div>
  );
};

export default CollegeTotalStudents;


